import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save, RotateCcw, Clock, AlertCircle, AlertTriangle, CheckCircle, ShieldAlert, Plus, Trash2, ArrowRight, Lock, Eye } from 'lucide-react';
import Button from './ui/Button';
import modelPolicyApiService from '../services/modelPolicyApiService';
import { trackEvent, trackException } from '../services/appInsightsService';
import './ModelPolicySection.css';

/**
 * ModelPolicySection — the one place a model is chosen for every future session.
 *
 * Backed by GET / PUT / POST-validate on /api/platform/model-policy. Implements the
 * binding mockup at docs/mockups/engine-model-policy/, whose element-to-symbol map is
 * recorded in the sprint evidence.
 *
 * Two things the operator must be able to see, because both are surprising otherwise:
 * a save reaches running engines when their own cache expires (bounded, stated in the
 * banner), and sessions already in flight keep the policy they were pinned with. The
 * engine decides once, at WebSocket open, so an edit is a change to what starts NEXT.
 *
 * English-only is the AdminPortal norm (no i18n framework here, user-authorized
 * 2026-09-03); this panel is Korean because the binding mockup is, and the operator
 * copy has to match what was reviewed.
 */

/** Tiers the policy addresses, in display order. Mirrors ModelPolicyDocument. */
export const POLICY_TIERS = ['normal', 'advanced'];

const TIER_LABEL = { normal: 'Normal', advanced: 'Advanced' };
const PROVIDERS = [
    { key: 'gpt', label: 'OpenAI GPT', id: 'openai' },
    { key: 'gemini', label: 'Gemini', id: 'gemini' },
];
const SLOTS = [
    { key: 'partial', label: '부분 번역', hint: '발화 중' },
    { key: 'final', label: '확정 번역', hint: '문장 완성 시' },
    { key: 'fallback', label: '첫 순차 대체 모델', hint: 'pair 실패 후' },
];
/** What an empty fallback resolves to, per provider — stated so the blank is not read as "nothing happens". */
const FALLBACK_NOTE = {
    gpt: '비우면 확정 모델에서 순차 연결을 따라갑니다.',
    gemini: '비우면 해당 등급의 GPT 확정 모델부터 순차 실행합니다.',
};
const MAX_FINAL_STAGES = 5;

/**
 * The AI 기능 card's rows, in display order. Mirrors ModelPolicyAiFeatures
 * (summary/qa/translate/glossaryExtraction) — this key is also the dotted-path segment the validator uses
 * (`aiFeatures.<key>.*`), so it doubles as the lookup key for violations and warnings.
 */
const AI_FEATURES = [
    { key: 'summary', label: 'AI Summary' },
    { key: 'qa', label: 'Q&A' },
    { key: 'translate', label: 'PDF·Audio 번역' },
    { key: 'glossaryExtraction', label: 'Glossary 용어 추출', provider: 'gemini', supportsFallback: false },
];

/**
 * Partial pacing modes and the shipped defaults. The server fills these in for any stored
 * document that predates the field, so they are only a display fallback here — the value that
 * runs is whatever the GET returned.
 */
const PARTIAL_DISPATCH = [
    { value: 'latest-wins', label: '최신 우선 · 동시 1건 (권장)' },
    { value: 'legacy', label: '기존 적응형 조절 · 동시 여러 건' },
];
const DEFAULT_PARTIALS = { dispatch: 'latest-wins', minIntervalMs: 1500 };
const MAX_PARTIAL_INTERVAL_MS = 10000;

const clone = (value) => JSON.parse(JSON.stringify(value));

/** Reads a tier's slots for a provider, tolerating an absent Gemini section. */
const tierOf = (policy, provider, tier) =>
    (provider === 'gpt' ? policy?.gpt?.[tier] : policy?.gemini?.tiers?.[tier]) || {};

/** Edges are stored as a from→to map; the editor needs ordered rows it can add to. */
const rowsFromEdges = (edges) => Object.entries(edges || {}).map(([from, to]) => [from, to]);
const edgesFromRows = (rows) =>
    rows.reduce((map, [from, to]) => (from && to ? { ...map, [from]: to } : map), {});
/** A repeated left-hand model would silently collapse in that map, so say so instead. */
const hasDuplicate = (values) => {
    const present = values.filter(Boolean);
    return new Set(present).size !== present.length;
};

/**
 * Flattens a policy to dotted leaf paths so a change review can never miss a field.
 * A hand-written list of "things to compare" silently stops covering whatever is added
 * to ModelPolicyDocument next; walking the object cannot.
 */
const flatten = (value, prefix = '', out = {}) => {
    if (value === null || value === undefined) {
        out[prefix] = '없음';
    } else if (Array.isArray(value)) {
        out[prefix] = value.length ? value.join(', ') : '없음';
    } else if (typeof value === 'object') {
        Object.keys(value).forEach((key) => flatten(value[key], prefix ? `${prefix}.${key}` : key, out));
    } else if (value === '') {
        out[prefix] = '없음';
    } else {
        out[prefix] = String(value);
    }
    return out;
};

/** Paths that describe the document itself rather than a decision the operator made. */
const NOT_A_CHANGE = ['version', 'etag', '_etag', 'id', 'tenantId', 'documentType',
    'lastModifiedAtUtc', 'lastModifiedBy'];

/**
 * Before/after rows between two policies, including fields present on only one side —
 * adding a Gemini tier or deleting a ladder edge is a change, and comparing only shared
 * keys would show neither.
 */
const changeRows = (before, after) => {
    const a = flatten(before || {});
    const b = flatten(after || {});
    return Array.from(new Set([...Object.keys(a), ...Object.keys(b)]))
        .filter((path) => !NOT_A_CHANGE.includes(path))
        .filter((path) => a[path] !== b[path])
        .sort()
        .map((path) => ({ path, before: a[path] ?? '없음', after: b[path] ?? '없음' }));
};

/** Mirrors the resolver's pin-time flattening without claiming this browser preview is a pin. */
const executionRoutesFor = (policy, tier, supportedModels) => {
    const gptTier = policy?.gpt?.[tier] || {};
    const edges = policy?.ladder?.edges || {};
    const metadata = (model) => supportedModels.find((candidate) => candidate.id === model) || {};
    const providerLabel = (model) => metadata(model).provider === 'gemini' ? 'Gemini' : 'OpenAI GPT';

    const route = (provider, configured) => {
        const models = [];
        const seen = new Set();
        const add = (model) => {
            if (model && !seen.has(model) && models.length < MAX_FINAL_STAGES) {
                seen.add(model);
                models.push(model);
            }
        };

        add(configured.final);
        add(configured.fallback || (provider === 'Gemini' ? gptTier.final : null));
        let cursor = models[models.length - 1];
        const traversed = new Set();
        while (cursor && edges[cursor] && !traversed.has(cursor) && models.length < MAX_FINAL_STAGES) {
            traversed.add(cursor);
            const next = edges[cursor];
            add(next);
            cursor = next;
        }

        return {
            provider,
            partial: configured.partial || configured.final || '미설정',
            partialInference: configured.partialThinking
                || metadata(configured.partial || configured.final).defaultThinking || '기본값',
            stages: models.map((model, index) => ({
                model,
                provider: providerLabel(model),
                inference: index === 0
                    ? configured.finalThinking || metadata(model).defaultThinking || '기본값'
                    : metadata(model).defaultThinking || '기본값',
            })),
        };
    };

    const routes = [route('OpenAI GPT', gptTier)];
    const geminiTier = policy?.gemini?.tiers?.[tier];
    if (policy?.gemini?.enabled && geminiTier?.final) {
        routes.push(route('Gemini', geminiTier));
    }
    return routes;
};

const ModelPolicySection = ({ readOnly = false }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [policy, setPolicy] = useState(null);
    const [baseline, setBaseline] = useState(null);
    const [meta, setMeta] = useState(null);
    const [violations, setViolations] = useState([]);
    // Advisory findings from the server's last answer, and which policy that answer was about.
    // They never block a save. 'stored' comes from GET, so every open of the panel shows the
    // warnings of the policy actually in force; 'draft' comes from reviewing unsaved edits.
    const [warnings, setWarnings] = useState([]);
    const [warningsFor, setWarningsFor] = useState('stored');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(null);
    const [conflict, setConflict] = useState(false);
    const [edgeRows, setEdgeRows] = useState([]);
    const [activeTier, setActiveTier] = useState(POLICY_TIERS[0]);
    const [reviewing, setReviewing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        setConflict(false);
        try {
            const result = await modelPolicyApiService.getModelPolicy();
            setPolicy(clone(result.policy));
            setBaseline(clone(result.policy));
            setEdgeRows(rowsFromEdges(result.policy?.ladder?.edges));
            setMeta(result);
            setViolations([]);
            setWarnings(result.warnings || []);
            setWarningsFor('stored');
            setSaved(null);
            trackEvent('model_policy_view', {
                source: result.source,
                version: result.version,
                warningCodes: (result.warnings || []).map((w) => w.code).join(','),
            });
        } catch (error) {
            // A read failure is not the same as an empty policy: showing defaults
            // here would invite an operator to "fix" a policy they cannot see.
            setLoadError(error.message);
            trackException(error, { component: 'ModelPolicySection', operation: 'load' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const dirty = useMemo(
        () => Boolean(policy) && JSON.stringify(policy) !== JSON.stringify(baseline),
        [policy, baseline]);

    const violationFor = useCallback(
        (path) => violations.find((v) => v.path === path), [violations]);
    const warningFor = useCallback(
        (path) => warnings.find((w) => w.path === path), [warnings]);

    /** Every edit funnels through here so a stale success banner never outlives the draft. */
    const edit = (mutate) => {
        setPolicy((current) => {
            const next = clone(current);
            mutate(next);
            return next;
        });
        setSaved(null);
    };

    const setSlot = (provider, tier, slot, value) => edit((next) => {
        const bucket = provider === 'gpt'
            ? (next.gpt = next.gpt || {})
            : ((next.gemini = next.gemini || {}), (next.gemini.tiers = next.gemini.tiers || {}));
        bucket[tier] = { ...(bucket[tier] || {}), [slot]: value || null };
    });

    const setGemini = (field, value) => edit((next) => {
        next.gemini = { ...(next.gemini || {}), [field]: value };
    });

    /** Gemini serves a tier iff that tier has an entry — presence IS the targeting. */
    const toggleGeminiTier = (tier, on) => edit((next) => {
        next.gemini = next.gemini || {};
        next.gemini.tiers = { ...(next.gemini.tiers || {}) };
        if (on) {
            // Seeded empty on purpose: the catalogue lives in the backend, and
            // guessing model ids here would put a second copy of it in the browser.
            next.gemini.tiers[tier] = next.gemini.tiers[tier] || {};
        } else {
            delete next.gemini.tiers[tier];
        }
    });

    const setLadder = (field, value) => edit((next) => {
        next.ladder = { ...(next.ladder || {}), [field]: value };
    });

    const applyEdgeRows = (rows) => {
        setEdgeRows(rows);
        edit((next) => {
            next.ladder = { ...(next.ladder || {}), edges: edgesFromRows(rows) };
        });
    };

    const setPartials = (field, value) => edit((next) => {
        next.partials = { ...DEFAULT_PARTIALS, ...(next.partials || {}), [field]: value };
    });

    const setLegacy = (rows) => edit((next) => {
        next.race = { ...(next.race || {}), legacyFallbacks: rows };
    });

    const allModels = meta?.supportedModels || [];
    const modelsFor = (providerId) => allModels.filter((model) => model.provider === providerId);
    const thinkingFor = (modelId) => allModels.find((model) => model.id === modelId)?.thinking || [];
    /**
     * What the PROVIDER applies when a request omits reasoning entirely (SupportedModel.
     * ApiDefaultReasoning) — shown so "모델 기본값" is not a blank promise. Null/absent for an
     * uncatalogued model or one the repo has no verified default for (Gemini today).
     */
    const apiDefaultReasoningFor = (modelId) =>
        allModels.find((model) => model.id === modelId)?.apiDefaultReasoning;
    const defaultReasoningLabel = (modelId) => {
        const known = apiDefaultReasoningFor(modelId);
        return known ? `모델 기본값 (${known})` : '모델 기본값';
    };

    /**
     * What one AI feature actually runs on right now: the stored slot if the operator (or an
     * earlier save) set one, else the compiled default GET reported alongside the policy. Never
     * writes the default anywhere — it is read-only until an edit touches this feature.
     */
    const aiFeatureEffective = (feature) =>
        policy?.aiFeatures?.[feature] || meta?.aiFeatureDefaults?.[feature] || {};

    /**
     * Turns a display value into a stored one. The touched feature is seeded from whatever was
     * ON SCREEN (stored slot or compiled default) so changing only the model does not blank out
     * a reasoning or fallback the operator was not looking at; every other feature and the rest
     * of the document ride along untouched because `edit` clones the current policy whole.
     */
    const setAiFeature = (feature, field, value) => edit((next) => {
        next.aiFeatures = next.aiFeatures || {};
        const current = next.aiFeatures[feature] || aiFeatureEffective(feature);
        next.aiFeatures[feature] = { ...current, [field]: value || null };
        // No fallback model → no fallback reasoning. The select is disabled then, so a stale value
        // would be invisible yet still sent, and the server refuses it (V12).
        if (field === 'fallback' && !value) {
            next.aiFeatures[feature].fallbackReasoning = null;
        }
    });

    const save = async () => {
        setSaving(true);
        setConflict(false);
        try {
            // Validate first so the operator sees every problem at once rather than
            // one per attempt. The server validates again — this is for the editor.
            const verdict = await modelPolicyApiService.validateModelPolicy(policy);
            setViolations(verdict.violations || []);
            setWarnings(verdict.warnings || []);
            setWarningsFor('draft');
            if (!verdict.valid) {
                trackEvent('model_policy_validation_failed', {
                    violationCount: (verdict.violations || []).length,
                    codes: (verdict.violations || []).map((v) => v.code).join(','),
                });
                return;
            }

            const result = await modelPolicyApiService.saveModelPolicy(
                policy, meta?.source === 'stored' ? meta.etag : '*');
            setPolicy(clone(result.policy));
            setBaseline(clone(result.policy));
            setEdgeRows(rowsFromEdges(result.policy?.ladder?.edges));
            setMeta({ ...meta, ...result, source: 'stored' });
            setWarnings(result.warnings || []);
            setWarningsFor('stored');
            setSaved(result);
            setReviewing(false);
            trackEvent('model_policy_save', { version: result.version });
        } catch (error) {
            if (error.status === 409) {
                // Someone else saved while this edit was open. Reloading is the only
                // safe move: retrying would write onto a document nobody reviewed.
                setConflict(true);
            } else if (error.status === 400 && error.violations?.length) {
                setViolations(error.violations);
            } else {
                setLoadError(error.message);
            }
            trackException(error, { component: 'ModelPolicySection', operation: 'save' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="model-policy-section" role="status" aria-busy="true">모델 정책을 불러오는 중…</div>;
    }

    if (loadError && !policy) {
        return (
            <div className="model-policy-section" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span> 모델 정책을 불러오지 못했습니다. {loadError}</span>
                <Button onClick={load}>다시 시도</Button>
            </div>
        );
    }

    const geminiOn = Boolean(policy?.gemini?.enabled);
    const legacyRows = policy?.race?.legacyFallbacks || [];
    const pending = changeRows(baseline, policy);
    const executionRoutes = executionRoutesFor(policy, activeTier, meta?.supportedModels || []);

    const modelOptions = (models) => models.map((model) => (
        <option key={model.id} value={model.id}>{model.id}</option>
    ));

    return (
        <section className="model-policy-section" data-testid="model-policy-section" aria-label="모델 정책">
            <header>
                <h2>모델 정책</h2>
                <p>번역 모델과 장애 시 대체 경로를 한곳에서 관리하세요.</p>
            </header>

            {/* Stated rather than assumed: an operator's first question after saving is
                when it takes effect, and the honest answer has two halves. */}
            <p className="model-policy-propagation">
                <Clock size={14} aria-hidden="true" />
                {' '}저장한 정책은 새 세션부터 적용됩니다. 진행 중인 세션은 유지됩니다.
                {' '}정상 조회 시 최대 {meta?.enginePropagationSeconds ?? 60}초.
            </p>

            {meta?.source === 'compiled-default' && (
                <p className="model-policy-source" role="note">
                    저장된 정책이 없어 코드 기본값을 표시합니다. 저장하면 처음 생성됩니다.
                </p>
            )}

            {conflict && (
                <div className="model-policy-conflict" role="alert">
                    <ShieldAlert size={16} aria-hidden="true" />
                    <span> 다른 관리자가 먼저 저장했습니다. 다시 불러온 뒤 변경을 적용하세요.</span>
                    <Button onClick={load}>다시 불러오기</Button>
                </div>
            )}

            {readOnly && (
                <p className="model-policy-readonly" role="note">
                    <Lock size={14} aria-hidden="true" />
                    {' '}읽기 전용 권한입니다. 정책을 볼 수는 있지만 저장할 수 없습니다.
                </p>
            )}

            {/* One disabled fieldset rather than a `disabled` prop on each control: a
                control added later is covered without anyone remembering to gate it. */}
            <fieldset className="model-policy-editable" disabled={readOnly}>
            <fieldset className="model-policy-gemini">
                <legend>Gemini 배분</legend>
                <label>
                    <input
                        type="checkbox"
                        checked={geminiOn}
                        onChange={(e) => setGemini('enabled', e.target.checked)}
                    />
                    {' '}활성
                </label>
                <label htmlFor="model-policy-cohort">대상 세션 비율 (%)</label>
                <input
                    id="model-policy-cohort"
                    type="range"
                    min="0"
                    max="100"
                    value={policy?.gemini?.cohortPercent ?? 100}
                    disabled={!geminiOn}
                    onChange={(e) => setGemini('cohortPercent', Number(e.target.value))}
                />
                <output htmlFor="model-policy-cohort">
                    {policy?.gemini?.cohortPercent ?? 100}% · 나머지는 GPT
                </output>
                <p className="model-policy-hint">
                    Precise 공급자 정책이 all이면 이 비율이 음성 인식도 정합니다: 대상 세션은 Gemini, 나머지는 OpenAI.
                </p>
                {violationFor('gemini.cohortPercent') && (
                    <span role="alert">{violationFor('gemini.cohortPercent').message}</span>
                )}

                <p className="model-policy-hint">적용할 품질 등급</p>
                {POLICY_TIERS.map((tier) => (
                    <label key={tier}>
                        <input
                            type="checkbox"
                            checked={Boolean(policy?.gemini?.tiers?.[tier])}
                            disabled={!geminiOn}
                            onChange={(e) => toggleGeminiTier(tier, e.target.checked)}
                        />
                        {' '}{TIER_LABEL[tier]}
                    </label>
                ))}
                {violationFor('gemini.tiers') && (
                    <span role="alert">{violationFor('gemini.tiers').message}</span>
                )}
            </fieldset>

            <div className="model-policy-tierbar" role="tablist" aria-label="품질 등급">
                {POLICY_TIERS.map((tier) => (
                    <button
                        key={tier}
                        type="button"
                        role="tab"
                        id={`tier-tab-${tier}`}
                        aria-selected={activeTier === tier}
                        aria-controls={`tier-panel-${tier}`}
                        className={activeTier === tier ? 'is-active' : ''}
                        onClick={() => setActiveTier(tier)}
                    >
                        {TIER_LABEL[tier]}
                    </button>
                ))}
            </div>

            {POLICY_TIERS.filter((tier) => tier === activeTier).map((tier) => (
                <table
                    key={tier}
                    className="model-policy-slots"
                    id={`tier-panel-${tier}`}
                    role="tabpanel"
                    aria-labelledby={`tier-tab-${tier}`}
                >
                    <caption>{TIER_LABEL[tier]} 슬롯</caption>
                    <thead>
                        <tr>
                            <th scope="col">번역 단계</th>
                            {PROVIDERS.map((provider) => (
                                <th key={provider.key} scope="col">{provider.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {SLOTS.map((slot) => (
                            <tr key={slot.key}>
                                <th scope="row">
                                    {slot.label}
                                    <span className="model-policy-hint"> {slot.hint}</span>
                                </th>
                                {PROVIDERS.map((provider) => {
                                    // Editing Gemini slots for a tier Gemini does not serve
                                    // stores configuration nothing will ever read, and the
                                    // operator has no way to tell that from a real setting.
                                    const inactive = provider.key === 'gemini'
                                        && !(geminiOn && policy?.gemini?.tiers?.[tier]);
                                    const path = `${provider.key}.${tier}.${slot.key}`;
                                    const violation = violationFor(path);
                                    const warning = warningFor(path);
                                    const inputId = `slot-${provider.key}-${tier}-${slot.key}`;
                                    const warningId = `${inputId}-warning`;
                                    const selected = tierOf(policy, provider.key, tier)[slot.key] || '';
                                    const thinkingKey = `${slot.key}Thinking`;
                                    const thinkingId = `${inputId}-thinking`;
                                    const thinkingViolation = violationFor(
                                        `${provider.key}.${tier}.${thinkingKey}`);
                                    return (
                                        <td key={provider.key} className={inactive ? 'is-inactive' : undefined}>
                                            <span className="model-policy-provider-label">{provider.label}</span>
                                            <label className="visually-hidden" htmlFor={inputId}>
                                                {`${TIER_LABEL[tier]} ${provider.label} ${slot.label}`}
                                            </label>
                                            <select
                                                id={inputId}
                                                value={selected}
                                                disabled={inactive}
                                                aria-invalid={violation ? 'true' : undefined}
                                                aria-describedby={warning ? warningId : undefined}
                                                data-warning={warning ? 'true' : undefined}
                                                onChange={(e) => setSlot(provider.key, tier, slot.key, e.target.value)}
                                            >
                                                <option value="">사용 안 함</option>
                                                {/* Sequential fallbacks may stay on the provider or cross it.
                                                    Primary and Partial remain provider-specific slots. */}
                                                {modelOptions(slot.key === 'fallback'
                                                    ? allModels
                                                    : modelsFor(provider.id))}
                                            </select>
                                            {violation && <span role="alert">{violation.message}</span>}
                                            {/* A warning, not an error: the value saves and runs. It
                                                is described-by the select rather than announced as an
                                                alert, because nothing here needs fixing. */}
                                            {/* Folded to an icon so the sentence does not push the
                                                reasoning select down; the text opens on hover, focus, or
                                                tap (Safari does not focus a clicked button, hence onClick). */}
                                            {warning && (
                                                <span className="model-policy-warning-anchor">
                                                    <button
                                                        type="button"
                                                        className="model-policy-warning-icon"
                                                        aria-label="경고 보기"
                                                        aria-describedby={warningId}
                                                        onClick={(e) => e.currentTarget.focus()}
                                                    >
                                                        <AlertTriangle size={16} aria-hidden="true" />
                                                    </button>
                                                    <span id={warningId} role="tooltip" className="model-policy-warning-tip">
                                                        경고: {warning.message}
                                                    </span>
                                                </span>
                                            )}

                                            {slot.key === 'fallback' ? (
                                                <p className="model-policy-hint">{FALLBACK_NOTE[provider.key]}</p>
                                            ) : (
                                                <>
                                                    {/* Per-model vocabulary, not a shared enum: the same word
                                                        is a 400 on one model and valid on the next. */}
                                                    <label className="visually-hidden" htmlFor={thinkingId}>
                                                        {`${TIER_LABEL[tier]} ${provider.label} ${slot.label} 추론 수준`}
                                                    </label>
                                                    <select
                                                        id={thinkingId}
                                                        value={tierOf(policy, provider.key, tier)[thinkingKey] || ''}
                                                        disabled={inactive || !selected}
                                                        aria-invalid={thinkingViolation ? 'true' : undefined}
                                                        onChange={(e) =>
                                                            setSlot(provider.key, tier, thinkingKey, e.target.value)}
                                                    >
                                                        <option value="">모델 기본값</option>
                                                        {thinkingFor(selected).map((level) => (
                                                            <option key={level} value={level}>{level}</option>
                                                        ))}
                                                    </select>
                                                    {thinkingViolation && (
                                                        <span role="alert">{thinkingViolation.message}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ))}

            {!(geminiOn && policy?.gemini?.tiers?.[activeTier]) && (
                <p className="model-policy-hint" role="note">
                    이 등급은 GPT만 사용합니다. 위 Gemini 배분에서 {TIER_LABEL[activeTier]}을(를) 선택하면 Gemini 슬롯을 편집할 수 있습니다.
                </p>
            )}

            {/* AI 기능: AI Summary, Q&A, Content Library(PDF·Audio) 번역과 Glossary 용어 추출의 모델·추론·fallback.
                Realtime 번역 표와는 독립된 설정이라 등급 탭의 영향을 받지 않는다. 각 기능의 모델과
                fallback 선택지는 위 GET이 이미 내려준 해당 provider 카탈로그에서 온다. */}
            <section className="model-policy-ai-features-card" aria-label="AI 기능">
                <h3>AI 기능</h3>
                <p className="model-policy-hint">
                    AI Summary, Q&amp;A, PDF·Audio 번역과 Glossary 용어 추출이 각각 어떤 모델과 추론 수준으로 실행되는지,
                    실패 시 어떤 모델로 한 번 더 시도할지 설정하세요. 비워두면 위 표시된 기본값 그대로 동작합니다.
                </p>
                <table className="model-policy-ai-features">
                    <thead>
                        <tr>
                            <th scope="col">기능</th>
                            <th scope="col">모델</th>
                            <th scope="col">추론 수준</th>
                            <th scope="col">Fallback</th>
                            <th scope="col">Fallback 추론</th>
                        </tr>
                    </thead>
                    <tbody>
                        {AI_FEATURES.map(({ key, label, provider = 'openai', supportsFallback = true }) => {
                            const effective = aiFeatureEffective(key);
                            const modelPath = `aiFeatures.${key}.model`;
                            const reasoningPath = `aiFeatures.${key}.reasoning`;
                            const fallbackPath = `aiFeatures.${key}.fallback`;
                            const fallbackReasoningPath = `aiFeatures.${key}.fallbackReasoning`;
                            const modelViolation = violationFor(modelPath);
                            const modelWarning = warningFor(modelPath);
                            const reasoningViolation = violationFor(reasoningPath);
                            const fallbackViolation = violationFor(fallbackPath);
                            const fallbackWarning = warningFor(fallbackPath);
                            const fallbackReasoningViolation = violationFor(fallbackReasoningPath);
                            const modelId = `ai-feature-${key}-model`;
                            const reasoningId = `ai-feature-${key}-reasoning`;
                            const fallbackId = `ai-feature-${key}-fallback`;
                            const fallbackReasoningId = `ai-feature-${key}-fallback-reasoning`;
                            const modelWarningId = `${modelId}-warning`;
                            const fallbackWarningId = `${fallbackId}-warning`;
                            const models = modelsFor(provider);
                            // D2/V15 applies to summary and qa only; translate is exempt (see
                            // ModelPolicyValidator's IsSubjectToRiskyReasoningRule).
                            const isRiskyReasoningFeature = key === 'summary' || key === 'qa';

                            return (
                                <tr key={key}>
                                    <th scope="row">{label}</th>
                                    <td>
                                        <label className="visually-hidden" htmlFor={modelId}>
                                            {`${label} 모델`}
                                        </label>
                                        <select
                                            id={modelId}
                                            value={effective.model || ''}
                                            aria-invalid={modelViolation ? 'true' : undefined}
                                            aria-describedby={modelWarning ? modelWarningId : undefined}
                                            data-warning={modelWarning ? 'true' : undefined}
                                            onChange={(e) => setAiFeature(key, 'model', e.target.value)}
                                        >
                                            {modelOptions(models)}
                                        </select>
                                        {modelViolation && <span role="alert">{modelViolation.message}</span>}
                                        {modelWarning && (
                                            <span className="model-policy-warning-anchor">
                                                <button
                                                    type="button"
                                                    className="model-policy-warning-icon"
                                                    aria-label="경고 보기"
                                                    aria-describedby={modelWarningId}
                                                    onClick={(e) => e.currentTarget.focus()}
                                                >
                                                    <AlertTriangle size={16} aria-hidden="true" />
                                                </button>
                                                <span id={modelWarningId} role="tooltip" className="model-policy-warning-tip">
                                                    경고: {modelWarning.message}
                                                </span>
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <label className="visually-hidden" htmlFor={reasoningId}>
                                            {`${label} 추론 수준`}
                                        </label>
                                        <select
                                            id={reasoningId}
                                            value={effective.reasoning || ''}
                                            disabled={!effective.model}
                                            aria-invalid={reasoningViolation ? 'true' : undefined}
                                            onChange={(e) => setAiFeature(key, 'reasoning', e.target.value)}
                                        >
                                            <option value="">{defaultReasoningLabel(effective.model)}</option>
                                            {thinkingFor(effective.model).map((level) => (
                                                <option key={level} value={level}>{level}</option>
                                            ))}
                                        </select>
                                        {reasoningViolation && <span role="alert">{reasoningViolation.message}</span>}
                                        {/* D2/V15: measured 2026-09-25 — a reasoning-family model spends its
                                            output budget on reasoning before writing an answer, and Q&A/summary
                                            come back empty at medium and above. Shown always, not only after a
                                            violation, so the constraint is visible before an operator hits it. */}
                                        {isRiskyReasoningFeature && (
                                            <p className="model-policy-hint">
                                                {'Q&A·요약은 none 또는 low만 저장할 수 있습니다 (실측: medium 이상은 효과 없이 느리고 비쌈)'}
                                            </p>
                                        )}
                                    </td>
                                    {supportsFallback ? <>
                                    <td>
                                        <label className="visually-hidden" htmlFor={fallbackId}>
                                            {`${label} Fallback`}
                                        </label>
                                        <select
                                            id={fallbackId}
                                            value={effective.fallback || ''}
                                            aria-invalid={fallbackViolation ? 'true' : undefined}
                                            aria-describedby={fallbackWarning ? fallbackWarningId : undefined}
                                            data-warning={fallbackWarning ? 'true' : undefined}
                                            onChange={(e) => setAiFeature(key, 'fallback', e.target.value)}
                                        >
                                            <option value="">없음</option>
                                            {modelOptions(models)}
                                        </select>
                                        {!effective.fallback && (
                                            <p className="model-policy-hint">실패 시 대체 모델 없음</p>
                                        )}
                                        {fallbackViolation && <span role="alert">{fallbackViolation.message}</span>}
                                        {fallbackWarning && (
                                            <span className="model-policy-warning-anchor">
                                                <button
                                                    type="button"
                                                    className="model-policy-warning-icon"
                                                    aria-label="경고 보기"
                                                    aria-describedby={fallbackWarningId}
                                                    onClick={(e) => e.currentTarget.focus()}
                                                >
                                                    <AlertTriangle size={16} aria-hidden="true" />
                                                </button>
                                                <span id={fallbackWarningId} role="tooltip" className="model-policy-warning-tip">
                                                    경고: {fallbackWarning.message}
                                                </span>
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {/* Independent of the primary's reasoning (D3): null means "inherit
                                            the primary", so options are the FALLBACK model's own accepted
                                            values, not the primary's. Disabled with no fallback model because
                                            nothing would ever be sent this value (mirrors V12). */}
                                        <label className="visually-hidden" htmlFor={fallbackReasoningId}>
                                            {`${label} Fallback 추론`}
                                        </label>
                                        <select
                                            id={fallbackReasoningId}
                                            value={effective.fallbackReasoning || ''}
                                            disabled={!effective.fallback}
                                            aria-invalid={fallbackReasoningViolation ? 'true' : undefined}
                                            onChange={(e) => setAiFeature(key, 'fallbackReasoning', e.target.value)}
                                        >
                                            <option value="">주 모델과 같음</option>
                                            {thinkingFor(effective.fallback).map((level) => (
                                                <option key={level} value={level}>{level}</option>
                                            ))}
                                        </select>
                                        {fallbackReasoningViolation && (
                                            <span role="alert">{fallbackReasoningViolation.message}</span>
                                        )}
                                    </td>
                                    </> : (
                                        <td colSpan="2">
                                            <p className="model-policy-hint">Gemini 단일 호출</p>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            <fieldset className="model-policy-partials">
                <legend>부분 번역 전송</legend>
                <p className="model-policy-hint">
                    말하는 동안 나가는 부분 번역의 빈도입니다. 최신 우선은 번역을 동시에 1건만 보내고,
                    기다리는 동안 들어온 음성인식 결과는 가장 최신 것만 남깁니다.
                </p>
                <div className="model-policy-partials-row">
                    <label htmlFor="model-policy-partial-dispatch">전송 방식</label>
                    <select
                        id="model-policy-partial-dispatch"
                        value={policy?.partials?.dispatch ?? DEFAULT_PARTIALS.dispatch}
                        aria-invalid={violationFor('partials.dispatch') ? 'true' : undefined}
                        onChange={(e) => setPartials('dispatch', e.target.value)}
                    >
                        {PARTIAL_DISPATCH.map((mode) => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                        ))}
                    </select>
                    {violationFor('partials.dispatch') && (
                        <span role="alert">{violationFor('partials.dispatch').message}</span>
                    )}
                </div>
                <div className="model-policy-partials-row">
                    <label htmlFor="model-policy-partial-interval">최소 전송 간격 (ms)</label>
                    <input
                        id="model-policy-partial-interval"
                        type="number"
                        min={0}
                        max={MAX_PARTIAL_INTERVAL_MS}
                        step={100}
                        value={policy?.partials?.minIntervalMs ?? DEFAULT_PARTIALS.minIntervalMs}
                        disabled={(policy?.partials?.dispatch ?? DEFAULT_PARTIALS.dispatch) !== 'latest-wins'}
                        aria-describedby="model-policy-partial-interval-hint"
                        aria-invalid={violationFor('partials.minIntervalMs') ? 'true' : undefined}
                        onChange={(e) => setPartials('minIntervalMs', Number(e.target.value))}
                    />
                    {violationFor('partials.minIntervalMs') && (
                        <span role="alert">{violationFor('partials.minIntervalMs').message}</span>
                    )}
                </div>
                <p id="model-policy-partial-interval-hint" className="model-policy-hint">
                    이전 번역이 도착하고, 마지막 전송 후 이 시간이 지나야 다음 부분 번역을 보냅니다.
                    권장 1500ms — gpt-6-luna 부분 번역 응답이 p50 약 1.3초라 이보다 자주 보내도 화면이 더 빨리
                    바뀌지 않습니다. 0이면 응답이 오는 즉시 다음을 보냅니다. 기존 적응형 조절에서는 쓰이지 않습니다.
                </p>
            </fieldset>

            <details className="model-policy-advanced">
                <summary>순차 fallback 및 Legacy/non-server 설정</summary>

                <p className="model-policy-hint" role="note">
                    신규 server Mode B의 Final Stage 0은 같은 모델 Primary + Hedge 2회로 고정됩니다.
                    아래 동시 실행 수와 runtime demotion은 Legacy/non-server 호환 필드입니다.
                </p>
                <label htmlFor="model-policy-race">Legacy/non-server 동시 모델 수</label>
                <select
                    id="model-policy-race"
                    value={policy?.race?.maxActiveRace ?? 2}
                    onChange={(e) => edit((next) => {
                        next.race = { ...(next.race || {}), maxActiveRace: Number(e.target.value) };
                    })}
                >
                    <option value={1}>1개</option>
                    <option value={2}>2개</option>
                </select>
                {violationFor('race.maxActiveRace') && (
                    <span role="alert">{violationFor('race.maxActiveRace').message}</span>
                )}

                <label>
                    <input
                        type="checkbox"
                        checked={Boolean(policy?.ladder?.applyToPinnedSessions)}
                        onChange={(e) => setLadder('applyToPinnedSessions', e.target.checked)}
                    />
                    {' '}Legacy pinned 세션의 runtime demotion
                </label>
                {policy?.ladder?.applyToPinnedSessions && (
                    <p className="model-policy-hint" role="note">
                        이 호환 설정은 executionVersion 0 세션에만 적용됩니다.
                    </p>
                )}

                <h3>신규 server pin의 순차 fallback 연결</h3>
                <p className="model-policy-hint" role="note">
                    저장된 연결은 새 pin에서 ordered stage로 펼쳐집니다. 각 fallback은 이전 단계가 실패하거나 만료된 뒤 하나씩 실행됩니다.
                </p>
                {edgeRows.map(([from, to], index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`edge-${index}`} className="model-policy-edge">
                        <label className="visually-hidden" htmlFor={`edge-from-${index}`}>
                            {`전환 경로 ${index + 1} 시작 모델`}
                        </label>
                        <select
                            id={`edge-from-${index}`}
                            value={from}
                            onChange={(e) => applyEdgeRows(
                                edgeRows.map((row, i) => (i === index ? [e.target.value, row[1]] : row)))}
                        >
                            <option value="">선택</option>
                            {modelOptions(allModels)}
                        </select>
                        <ArrowRight size={14} aria-hidden="true" />
                        <label className="visually-hidden" htmlFor={`edge-to-${index}`}>
                            {`전환 경로 ${index + 1} 대체 모델`}
                        </label>
                        <select
                            id={`edge-to-${index}`}
                            value={to}
                            onChange={(e) => applyEdgeRows(
                                edgeRows.map((row, i) => (i === index ? [row[0], e.target.value] : row)))}
                        >
                            <option value="">선택</option>
                            {modelOptions(allModels)}
                        </select>
                        <Button
                            aria-label={`전환 경로 ${index + 1} 삭제`}
                            onClick={() => applyEdgeRows(edgeRows.filter((_, i) => i !== index))}
                        >
                            <Trash2 size={14} aria-hidden="true" />
                        </Button>
                    </div>
                ))}
                {hasDuplicate(edgeRows.map(([from]) => from)) && (
                    <span role="alert">시작 모델이 중복된 경로가 있습니다. 하나만 남습니다.</span>
                )}
                <Button onClick={() => applyEdgeRows([...edgeRows, ['', '']])}>
                    <Plus size={14} aria-hidden="true" /> 경로 추가
                </Button>
                {violationFor('ladder.edges') && (
                    <span role="alert">{violationFor('ladder.edges').message}</span>
                )}

                <h3>Legacy/non-server 연결의 대체 모델</h3>
                {legacyRows.length === 0 && <p className="model-policy-hint">설정된 대체 모델이 없습니다.</p>}
                {legacyRows.map((model, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`legacy-${index}`} className="model-policy-legacy">
                        <label className="visually-hidden" htmlFor={`legacy-${index}`}>
                            {`기존 연결 대체 모델 ${index + 1}`}
                        </label>
                        <select
                            id={`legacy-${index}`}
                            value={model}
                            onChange={(e) => setLegacy(
                                legacyRows.map((row, i) => (i === index ? e.target.value : row)))}
                        >
                            <option value="">선택</option>
                            {modelOptions(allModels)}
                        </select>
                        <Button
                            aria-label={`기존 연결 대체 모델 ${index + 1} 삭제`}
                            onClick={() => setLegacy(legacyRows.filter((_, i) => i !== index))}
                        >
                            <Trash2 size={14} aria-hidden="true" />
                        </Button>
                    </div>
                ))}
                {hasDuplicate(legacyRows) && (
                    <span role="alert">기존 연결의 대체 모델을 중복 없이 선택하세요.</span>
                )}
                <Button onClick={() => setLegacy([...legacyRows, ''])}>
                    <Plus size={14} aria-hidden="true" /> 모델 추가
                </Button>
                <p className="model-policy-hint">
                    Zoom도 server Mode B route이면 같은 모델 pair와 위 순차 chain을 사용합니다.
                    Legacy/non-server Zoom route만 이 호환 목록을 사용합니다.
                </p>
            </details>
            </fieldset>

            {violations.length > 0 && (
                <ul className="model-policy-violations" role="alert">
                    {violations.map((violation) => (
                        <li key={`${violation.code}-${violation.path}`}>
                            <strong>{violation.code}</strong> {violation.path}: {violation.message}
                        </li>
                    ))}
                </ul>
            )}

            {warnings.length > 0 && (
                <section
                    className="model-policy-warnings"
                    role="status"
                    aria-label={warningsFor === 'stored' ? '저장된 정책 경고' : '변경안 경고'}
                >
                    <h3>
                        <AlertTriangle size={14} aria-hidden="true" />
                        {' '}{warningsFor === 'stored'
                            ? `저장된 정책에 경고 ${warnings.length}건`
                            : `변경안에 경고 ${warnings.length}건`}
                    </h3>
                    <p className="model-policy-hint">
                        저장과 실행에는 문제가 없습니다. 의도한 설정인지 확인해 주세요.
                    </p>
                    <ul>
                        {warnings.map((warning) => (
                            <li key={`${warning.code}-${warning.path}`}>
                                <strong>{warning.code}</strong> {warning.path}: {warning.message}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="model-policy-preview" aria-label="구성된 서버 Mode B 실행 경로">
                <h3><Eye size={14} aria-hidden="true" /> 구성된 서버 Mode B 실행 경로</h3>
                <p className="model-policy-hint">
                    저장 정책에서 계산한 구성 경로이며 실제 세션 pin이 아닙니다.
                </p>
                {executionRoutes.map((route) => (
                    <section
                        key={route.provider}
                        className="model-policy-route"
                        aria-label={`${TIER_LABEL[activeTier]} ${route.provider} 구성 경로`}
                    >
                        <h4>{TIER_LABEL[activeTier]} · {route.provider} cohort</h4>
                        <p>
                            <strong>부분 번역 1회</strong>
                            {' '}{route.partial} · {route.provider} · {route.partialInference}
                        </p>
                        <ol>
                            {route.stages.map((stage, index) => (
                                <li key={stage.model}>
                                    <strong>Final Stage {index}</strong>
                                    {' '}{index === 0 ? 'Primary + 동일 모델 Hedge' : `Fallback ${index}`}
                                    {' · '}{stage.model}{index === 0 ? ' × 2' : ''}
                                    {' · '}{stage.provider} · {stage.inference}
                                    {' · '}{index === 0
                                        ? '동시에 2회, 먼저 쓸 수 있는 결과 1개만 채택'
                                        : '이전 단계가 모두 실패하거나 만료된 뒤 1회'}
                                </li>
                            ))}
                        </ol>
                        <p className="model-policy-hint">
                            {route.stages.length}개 모델 · 최대 {route.stages.length + 1}회 전송
                        </p>
                    </section>
                ))}
                <p className="model-policy-hint" role="note">
                    실제 세션 pin은 Translation Engine이 cohort·credential을 확인해 WebSocket 시작 시 고정합니다. 공유 용량 장부 설정은 적용 조건이 아닙니다.
                    실행 확인은 ModelPolicyPinned, V2TranslationSucceeded, V2TranslationAccepted를 같은 session·trace·segment로 연결합니다.
                </p>
            </section>

            <dl className="model-policy-meta" aria-label="정책 정보">
                <dt>저장된 버전</dt><dd>v{meta?.version ?? 0}</dd>
                {/* Stated, not inferred. Before this the only signal that a document existed
                    was that the compiled-default note was MISSING, and reading a fact off the
                    absence of another sentence is not reading it — least of all here, where
                    stored vs compiled-default decides whether the next save creates or
                    updates, and therefore whether the ETag sent is a value or `*`. */}
                <dt>상태</dt>
                <dd>{meta?.source === 'stored' ? '저장됨' : '코드 기본값'}</dd>
                <dt>마지막 수정</dt><dd>{policy?.lastModifiedAtUtc || '—'}</dd>
                <dt>수정한 사람</dt><dd>{policy?.lastModifiedBy || '—'}</dd>
            </dl>

            <div className="model-policy-actions">
                {saved && (
                    <span role="status">
                        <CheckCircle size={14} aria-hidden="true" />
                        {' '}v{saved.version} 저장됨 · {saved.appliesTo}
                    </span>
                )}
                {!saved && (
                    <span className="model-policy-hint">
                        {dirty ? `변경 ${pending.length}건` : '변경 없음'}
                    </span>
                )}
                <Button
                    onClick={() => {
                        setPolicy(clone(baseline));
                        setEdgeRows(rowsFromEdges(baseline?.ladder?.edges));
                        setViolations([]);
                        // Back to the stored policy, so back to ITS warnings.
                        setWarnings(meta?.warnings || []);
                        setWarningsFor('stored');
                        setSaved(null);
                    }}
                    disabled={readOnly || !dirty || saving}
                >
                    <RotateCcw size={14} aria-hidden="true" /> 되돌리기
                </Button>
                {/* Review, then save. A policy edit changes the model of every session
                    that starts afterwards, and a select is one keystroke away from a
                    change nobody meant to make. */}
                <Button onClick={() => setReviewing(true)} disabled={readOnly || !dirty || saving}>
                    변경 검토
                </Button>
            </div>

            {reviewing && (
                <div className="model-policy-review" role="dialog" aria-label="변경 검토" aria-modal="true">
                    <h3>변경 검토</h3>
                    {pending.length === 0 ? (
                        <p>첫 정책 생성 — 현재 표시된 값을 그대로 저장합니다.</p>
                    ) : (
                        <ul>
                            {pending.map((row) => (
                                <li key={row.path}>
                                    <strong>{row.path}</strong>
                                    {' '}<del>{row.before}</del> <ArrowRight size={12} aria-hidden="true" /> <ins>{row.after}</ins>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="model-policy-hint">
                        저장하면 새 세션부터 적용됩니다. 진행 중인 세션은 유지됩니다.
                    </p>
                    <div className="model-policy-actions">
                        <Button onClick={() => setReviewing(false)} disabled={saving}>취소</Button>
                        <Button onClick={save} disabled={saving}>
                            <Save size={14} aria-hidden="true" /> {saving ? '저장 중…' : '저장'}
                        </Button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ModelPolicySection;
